import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { serviceActivationsAPI } from '../services/api';
import {
  FireIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  MapPinIcon,
  UserIcon,
  ArchiveBoxIcon,
  XMarkIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner, Button, Table, Card, PermissionGate, ConfirmModal } from '../components/ui';
import { PageHeader, StatusBadge, ActionIconButton, DataTableCard, DataToolbar } from '../components/ui';
import { TableColumn } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';

interface ServiceActivation {
  id: number;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  deceasedName: string;
  deceasedType: 'CLIENT' | 'FAMILY_MEMBER';
  deceasedAge?: number | null;
  dateOfDeath: string;
  dateOfService?: string | null;
  serviceLocation?: string | null;
  chapelIncluded?: boolean;
  notes?: string | null;
  createdAt: string;
  completedAt?: string | null;
  clientPlan?: {
    client?: {
      id?: number;
      name: string;
      lastname: string;
      cedula: string;
    };
    planTemplate?: {
      name: string;
    };
  };
  casket?: {
    id?: number;
    model: string;
    isRental?: boolean;
    price?: number;
  };
}

const statusConfig: Record<string, { label: string; tone: 'success' | 'danger' | 'warning' | 'neutral' | 'info'; icon: React.FC<React.SVGProps<SVGSVGElement>> }> = {
  PENDING:     { label: 'Pendiente',   tone: 'info',    icon: ClockIcon },
  IN_PROGRESS: { label: 'En progreso', tone: 'warning',  icon: FireIcon },
  COMPLETED:   { label: 'Completado',  tone: 'success',  icon: CheckCircleIcon },
  CANCELLED:   { label: 'Cancelado',   tone: 'danger',   icon: XCircleIcon },
};

const STATUS_FLOW: Array<'PENDING' | 'IN_PROGRESS' | 'COMPLETED'> = ['PENDING', 'IN_PROGRESS', 'COMPLETED'];

/* ─── Detail panel ─── */
const DetailPanel: React.FC<{
  activation: ServiceActivation;
  onClose: () => void;
  onUpdateStatus: (id: number, status: string) => void;
  onCancel: (id: number) => void;
  canManage: boolean;
  canCancel: boolean;
}> = ({ activation, onClose, onUpdateStatus, onCancel, canManage, canCancel }) => {
  const cfg = statusConfig[activation.status] || statusConfig.PENDING;
  const clientName = activation.clientPlan?.client
    ? `${activation.clientPlan.client.name} ${activation.clientPlan.client.lastname}`.trim()
    : '—';

  const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString('es-VE', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

  const stepIndex = STATUS_FLOW.indexOf(activation.status as any);

  return (
    <>
      {/* Mobile backdrop */}
      <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={onClose} />
      <aside className="fixed inset-0 z-50 flex flex-col bg-white lg:static lg:inset-auto lg:z-auto lg:rounded-2xl lg:border lg:border-slate-200 lg:shadow-sm overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-5 py-4 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Detalle del servicio</h2>
            <p className="text-xs text-slate-500 mt-0.5">#{activation.id}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar panel"
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Status badge + progress */}
          <div className="space-y-3">
            <StatusBadge tone={cfg.tone}>{cfg.label}</StatusBadge>
            {activation.status !== 'CANCELLED' && (
              <div className="flex items-center gap-2">
                {STATUS_FLOW.map((s, i) => (
                  <React.Fragment key={s}>
                    <div className={`flex items-center gap-1.5 ${i <= stepIndex ? 'text-primary-600' : 'text-slate-400'}`}>
                      <div className={`h-2 w-2 rounded-full ${i <= stepIndex ? 'bg-primary-500' : 'bg-slate-200'}`} />
                      <span className="text-[10px] font-medium">{statusConfig[s].label}</span>
                    </div>
                    {i < STATUS_FLOW.length - 1 && (
                      <div className={`flex-1 h-px ${i < stepIndex ? 'bg-primary-400' : 'bg-slate-200'}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* Deceased info */}
          <section className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fallecido</h3>
            <div className="flex items-center gap-2">
              <UserIcon className="h-5 w-5 text-slate-400 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-900">{activation.deceasedName}</p>
                <p className="text-xs text-slate-500">
                  {activation.deceasedType === 'CLIENT' ? 'Titular del plan' : 'Familiar cubierto'}
                  {activation.deceasedAge ? ` · ${activation.deceasedAge} años` : ''}
                </p>
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 pt-1 text-sm">
              <div>
                <dt className="text-xs text-slate-500">Fecha de fallecimiento</dt>
                <dd className="font-medium text-slate-800">{fmtDate(activation.dateOfDeath)}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">Afiliado</dt>
                <dd className="font-medium text-slate-800 truncate">{clientName}</dd>
              </div>
              {activation.clientPlan?.client?.cedula && (
                <div>
                  <dt className="text-xs text-slate-500">Cédula</dt>
                  <dd className="font-medium text-slate-800">{activation.clientPlan.client.cedula}</dd>
                </div>
              )}
              {activation.clientPlan?.planTemplate?.name && (
                <div>
                  <dt className="text-xs text-slate-500">Plan</dt>
                  <dd className="font-medium text-slate-800 truncate">{activation.clientPlan.planTemplate.name}</dd>
                </div>
              )}
            </dl>
          </section>

          {/* Service info */}
          <section className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Servicio</h3>
            <dl className="grid grid-cols-1 gap-y-2 text-sm">
              <div className="flex items-start gap-2">
                <CalendarIcon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <dt className="text-xs text-slate-500">Fecha del servicio</dt>
                  <dd className="font-medium text-slate-800">{fmtDate(activation.dateOfService)}</dd>
                </div>
              </div>
              {activation.serviceLocation && (
                <div className="flex items-start gap-2">
                  <MapPinIcon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <dt className="text-xs text-slate-500">Ubicación</dt>
                    <dd className="font-medium text-slate-800">{activation.serviceLocation}</dd>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-2">
                <InformationCircleIcon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <dt className="text-xs text-slate-500">Capilla ardiente</dt>
                  <dd className="font-medium text-slate-800">{activation.chapelIncluded ? 'Incluida' : 'No incluida'}</dd>
                </div>
              </div>
            </dl>
          </section>

          {/* Casket */}
          {activation.casket && (
            <section className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ataúd asignado</h3>
              <div className="flex items-center gap-2">
                <ArchiveBoxIcon className="h-5 w-5 text-slate-400 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">{activation.casket.model}</p>
                  {activation.casket.isRental && (
                    <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-amber-200">
                      Renta — se libera al completar
                    </span>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Notes */}
          {activation.notes && (
            <section className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Notas</h3>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{activation.notes}</p>
            </section>
          )}

          {/* Dates */}
          <section className="text-xs text-slate-400 space-y-0.5">
            <p>Registrado: {fmtDate(activation.createdAt)}</p>
            {activation.completedAt && <p>Completado: {fmtDate(activation.completedAt)}</p>}
          </section>
        </div>

        {/* Actions */}
        {(canManage || canCancel) && (activation.status === 'PENDING' || activation.status === 'IN_PROGRESS') && (
          <div className="shrink-0 border-t border-slate-100 bg-white p-4 flex gap-2">
            {canManage && activation.status === 'PENDING' && (
              <Button
                variant="primary"
                size="sm"
                className="flex-1"
                onClick={() => onUpdateStatus(activation.id, 'IN_PROGRESS')}
              >
                <FireIcon className="h-4 w-4 mr-1.5" />
                Iniciar servicio
              </Button>
            )}
            {canManage && activation.status === 'IN_PROGRESS' && (
              <Button
                variant="primary"
                size="sm"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => onUpdateStatus(activation.id, 'COMPLETED')}
              >
                <CheckCircleIcon className="h-4 w-4 mr-1.5" />
                Marcar completado
              </Button>
            )}
            {canCancel && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => onCancel(activation.id)}
              >
                <XCircleIcon className="h-4 w-4 mr-1.5" />
                Cancelar
              </Button>
            )}
          </div>
        )}
      </aside>
    </>
  );
};

/* ─── Main page ─── */
const ServiceActivations: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedActivation, setSelectedActivation] = useState<ServiceActivation | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'danger' | 'success' | 'info';
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'warning', onConfirm: () => {} });

  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('service_activations.update');
  const canCancel = hasPermission('service_activations.cancel');

  const { data: activationsRes, isLoading } = useQuery<{ data: { data: ServiceActivation[] } }>(
    ['service-activations', statusFilter],
    () => serviceActivationsAPI.getAll(statusFilter ? { status: statusFilter } : undefined)
  );

  const { data: statsRes } = useQuery(
    'service-activations-stats',
    serviceActivationsAPI.getStats
  );

  const updateStatusMutation = useMutation(
    ({ id, status }: { id: number; status: string }) => serviceActivationsAPI.updateStatus(id, status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('service-activations');
        queryClient.invalidateQueries('service-activations-stats');
        toast.success('Estado actualizado');
        setSelectedActivation(null);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Error al actualizar el estado');
      },
    }
  );

  const cancelMutation = useMutation(
    (id: number) => serviceActivationsAPI.cancel(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('service-activations');
        queryClient.invalidateQueries('service-activations-stats');
        toast.success('Activación cancelada');
        setSelectedActivation(null);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Error al cancelar');
      },
    }
  );

  const handleUpdateStatus = (id: number, status: string) => {
    const labels: Record<string, string> = { IN_PROGRESS: 'en progreso', COMPLETED: 'completado' };
    setConfirmModal({
      isOpen: true,
      title: 'Actualizar estado',
      message: `¿Confirma marcar este servicio como ${labels[status] || status}?`,
      type: 'info',
      onConfirm: () => {
        updateStatusMutation.mutate({ id, status });
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleCancel = (id: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'Cancelar activación',
      message: '¿Está seguro de que desea cancelar esta activación? Esta acción no se puede deshacer.',
      type: 'danger',
      onConfirm: () => {
        cancelMutation.mutate(id);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const allActivations: ServiceActivation[] = activationsRes?.data?.data || [];

  const filtered = allActivations.filter(a => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    const clientName = `${a.clientPlan?.client?.name || ''} ${a.clientPlan?.client?.lastname || ''}`.toLowerCase();
    return (
      clientName.includes(q) ||
      a.deceasedName.toLowerCase().includes(q) ||
      (a.clientPlan?.client?.cedula || '').toLowerCase().includes(q) ||
      (a.serviceLocation || '').toLowerCase().includes(q)
    );
  });

  // Stats come back as flat { total, pending, inProgress, completed, cancelled }
  const statsData: Record<string, number> = statsRes?.data?.data || statsRes?.data || {};
  const countByStatus = (s: string) => {
    const keyMap: Record<string, string> = {
      PENDING: 'pending',
      IN_PROGRESS: 'inProgress',
      COMPLETED: 'completed',
      CANCELLED: 'cancelled',
    };
    return (statsData[keyMap[s]] as number) ?? 0;
  };

  const columns: TableColumn<ServiceActivation>[] = [
    {
      key: 'deceasedName',
      label: 'Fallecido',
      render: (value, row) => (
        <div>
          <div className="text-sm font-semibold text-slate-900">{value}</div>
          <div className="text-xs text-slate-500">
            {row.deceasedType === 'CLIENT' ? 'Titular' : 'Familiar'}
            {row.deceasedAge ? ` · ${row.deceasedAge} años` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'clientPlan',
      label: 'Afiliado',
      render: (_, row) => (
        <div>
          <div className="text-sm font-medium text-slate-900">
            {row.clientPlan?.client?.name} {row.clientPlan?.client?.lastname}
          </div>
          <div className="text-xs text-slate-500">{row.clientPlan?.client?.cedula || '—'}</div>
        </div>
      ),
    },
    {
      key: 'dateOfDeath',
      label: 'Fallecimiento',
      render: (value) => (
        <div className="flex items-center gap-1 text-sm text-slate-700">
          <CalendarIcon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          {value ? new Date(value).toLocaleDateString('es-VE') : '—'}
        </div>
      ),
    },
    {
      key: 'dateOfService',
      label: 'Fecha servicio',
      render: (value) => (
        <span className="text-sm text-slate-700">
          {value ? new Date(value).toLocaleDateString('es-VE') : '—'}
        </span>
      ),
    },
    {
      key: 'casket',
      label: 'Ataúd',
      render: (_, row) => (
        <span className="text-sm text-slate-700">
          {row.casket?.model || '—'}
          {row.casket?.isRental && (
            <span className="ml-1.5 text-[10px] font-semibold text-amber-700 bg-amber-50 rounded px-1 py-0.5">Renta</span>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (value) => {
        const cfg = statusConfig[value as string] || statusConfig.PENDING;
        return <StatusBadge tone={cfg.tone}>{cfg.label}</StatusBadge>;
      },
    },
    {
      key: 'id',
      label: 'Acciones',
      className: 'text-right',
      render: (_, row) => (
        <div className="flex justify-end items-center gap-1">
          <ActionIconButton
            title="Ver detalle"
            onClick={() => setSelectedActivation(row)}
            tone="info"
          >
            <EyeIcon className="h-4 w-4" />
          </ActionIconButton>
          {canManage && row.status === 'PENDING' && (
            <ActionIconButton
              title="Iniciar servicio"
              onClick={() => handleUpdateStatus(row.id, 'IN_PROGRESS')}
              tone="neutral"
            >
              <FireIcon className="h-4 w-4" />
            </ActionIconButton>
          )}
          {canManage && row.status === 'IN_PROGRESS' && (
            <ActionIconButton
              title="Marcar completado"
              onClick={() => handleUpdateStatus(row.id, 'COMPLETED')}
              tone="success"
            >
              <CheckCircleIcon className="h-4 w-4" />
            </ActionIconButton>
          )}
          {canCancel && (row.status === 'PENDING' || row.status === 'IN_PROGRESS') && (
            <ActionIconButton
              title="Cancelar"
              onClick={() => handleCancel(row.id)}
              tone="danger"
            >
              <XCircleIcon className="h-4 w-4" />
            </ActionIconButton>
          )}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Servicios Fúnebres"
        subtitle="Activaciones de servicios funerarios: seguimiento desde el registro hasta la conclusión."
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { status: 'PENDING',     label: 'Pendientes',   icon: ClockIcon,        bg: 'bg-blue-50',    ring: 'ring-blue-100',    iconCls: 'text-blue-600'    },
          { status: 'IN_PROGRESS', label: 'En progreso',  icon: FireIcon,         bg: 'bg-amber-50',   ring: 'ring-amber-100',   iconCls: 'text-amber-600'   },
          { status: 'COMPLETED',   label: 'Completados',  icon: CheckCircleIcon,  bg: 'bg-emerald-50', ring: 'ring-emerald-100', iconCls: 'text-emerald-600' },
          { status: 'CANCELLED',   label: 'Cancelados',   icon: XCircleIcon,      bg: 'bg-rose-50',    ring: 'ring-rose-100',    iconCls: 'text-rose-600'    },
        ].map(({ status, label, icon: Icon, bg, ring, iconCls }) => (
          <button
            key={status}
            type="button"
            onClick={() => setStatusFilter(s => s === status ? '' : status)}
            className={`group flex items-center gap-3 rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm text-left transition hover:border-slate-300 hover:shadow-md ${statusFilter === status ? 'ring-2 ring-primary-400' : ''}`}
          >
            <div className={`shrink-0 rounded-xl ${bg} p-2.5 ring-1 ${ring}`}>
              <Icon className={`h-5 w-5 ${iconCls}`} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide truncate">{label}</p>
              <p className="text-2xl font-bold text-slate-900">{countByStatus(status)}</p>
            </div>
          </button>
        ))}
      </div>

      <div className={`flex flex-col gap-5 ${selectedActivation ? 'lg:flex-row lg:items-start' : ''}`}>
        {/* Table side */}
        <div className="flex-1 min-w-0 space-y-4">
          <DataToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Buscar por fallecido, afiliado, cédula..."
            rightSlot={
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-500">Estado</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  aria-label="Filtrar por estado"
                >
                  <option value="">Todos</option>
                  <option value="PENDING">Pendiente</option>
                  <option value="IN_PROGRESS">En progreso</option>
                  <option value="COMPLETED">Completado</option>
                  <option value="CANCELLED">Cancelado</option>
                </select>
              </div>
            }
          />

          <DataTableCard>
            <Table
              data={filtered}
              columns={columns}
              loading={isLoading}
              emptyMessage="No se encontraron activaciones de servicio"
              rowClassName="hover:bg-slate-50 cursor-pointer"
              mobileTitleKey="deceasedName"
              mobileSubtitleKey="status"
              mobilePageSize={4}
            />
          </DataTableCard>
        </div>

        {/* Detail panel */}
        {selectedActivation && (
          <div className="lg:w-[380px] lg:shrink-0 lg:sticky lg:top-6 lg:max-h-[calc(100vh-140px)]">
            <DetailPanel
              activation={selectedActivation}
              onClose={() => setSelectedActivation(null)}
              onUpdateStatus={handleUpdateStatus}
              onCancel={handleCancel}
              canManage={canManage}
              canCancel={canCancel}
            />
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        isLoading={updateStatusMutation.isLoading || cancelMutation.isLoading}
      />
    </div>
  );
};

export default ServiceActivations;
